/**
 * Redaction for anything that is about to be logged or reported: a URL, a log
 * message that contains one, or a console argument (backlog c-059).
 *
 * The app's email links carry bearer secrets in their query: the reset token,
 * the verification and RSVP signatures, the Google callback's Sanctum token.
 * Logging the link puts the secret in the device log, and Sentry's console
 * breadcrumbs would carry it off the device. One predicate decides what is a
 * secret; every log line and the Sentry scrubbers go through it.
 *
 * Textual, not `new URL(...).searchParams`: it keeps the URL as written (no
 * re-encoding), works on a URL embedded in a longer message, and never throws
 * on a string that is not a URL.
 */

export const REDACTED = '[redacted]';

/** Query params whose value is, or protects, a credential. */
const SECRET_PARAMS = new Set([
  'token',
  'signature',
  'expires',
  'code',
  'state',
  'password',
  'secret',
  // The native Google sign-in's device nonce (services/google-app-nonce): it
  // is what makes the deep link's token acceptable to this device.
  'an',
]);

/** True for a param whose value must never be logged. */
export function isSecretParam(name: string): boolean {
  let key = name;
  try {
    key = decodeURIComponent(name);
  } catch {
    // A malformed escape: judge the raw name.
  }
  key = key.toLowerCase();
  return SECRET_PARAMS.has(key) || key.endsWith('_token') || key.endsWith('_secret');
}

/** `?name=value`, `&name=value` or `#name=value` anywhere in a string. */
const PARAM = /([?&#])([^=&#?\s]+)=([^&#\s"'<>]*)/g;

/**
 * The string with every secret query (or fragment) param's value replaced by
 * `[redacted]`; the path and the other params are left exactly as written.
 */
export function redactUrl(text: string): string {
  return text.replace(PARAM, (match, sep: string, name: string) =>
    isSecretParam(name) ? `${sep}${name}=${REDACTED}` : match,
  );
}

/** A link's params as a plain object for logging, secrets redacted. */
export function redactParams(params: URLSearchParams): Record<string, string> {
  const out: Record<string, string> = {};
  params.forEach((value, name) => {
    out[name] = isSecretParam(name) ? REDACTED : value;
  });
  return out;
}

/**
 * Any loggable value with its secrets redacted: strings through redactUrl,
 * object keys through isSecretParam, recursively (arrays and plain objects;
 * bounded depth, so a cyclic object cannot hang the reporter).
 */
export function redactValue(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') return redactUrl(value);
  if (depth >= 6 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => redactValue(item, depth + 1));
  const proto = Object.getPrototypeOf(value) as unknown;
  if (proto !== Object.prototype && proto !== null) return value;
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    out[key] = isSecretParam(key) ? REDACTED : redactValue(item, depth + 1);
  }
  return out;
}
